// src/components/communs/FormationPdf.jsx
import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import {
    X, Clock, ChevronLeft, ChevronRight,
    Maximize2, Minimize2, ZoomIn, ZoomOut, BookOpen,
    ChevronDown, ChevronUp, LogOut, Home
} from 'lucide-react';
import { Document, Page, pdfjs } from 'react-pdf';
// Import du worker via Vite (chemin absolu résolu)
import workerUrl from 'pdfjs-dist/build/pdf.worker.min.mjs?url';

// Définit le worker avec l'URL importée
pdfjs.GlobalWorkerOptions.workerSrc = workerUrl;

export default function FormationPdf({
    module,
    pdfUrl,
    pdfToken,
    formationTitre,
    moduleOrdre,
    totalModules,
    onClose,
    isOpen,
    onModuleChange,
    onComplete,
    mode = 'production',
    userRole = 'etudiant'
}) {
    console.log('🔍 [PDF] Composant rendu - mode:', mode, 'pdfToken:', !!pdfToken, 'pdfUrl:', pdfUrl);

    const loadProgressForModule = useCallback((moduleId) => {
        if (!moduleId) return { page: 1, time: 0, progress: 0 };
        const saved = localStorage.getItem(`module_progress_${moduleId}`);
        if (saved) {
            try {
                const data = JSON.parse(saved);
                return {
                    page: data.page || 1,
                    time: data.timeSpent || 0,
                    progress: data.progress || 0
                };
            } catch (e) {
                console.error("Erreur chargement progression:", e);
            }
        }
        return { page: 1, time: 0, progress: 0 };
    }, []);

    const [moduleKey, setModuleKey] = useState(module?.id);
    const [currentPage, setCurrentPage] = useState(1);
    const [timeSpent, setTimeSpent] = useState(0);
    const [progress, setProgress] = useState(0);
    const [totalPages, setTotalPages] = useState(0);
    const [isLoading, setIsLoading] = useState(true);
    const [isFullscreen, setIsFullscreen] = useState(false);
    const [zoom, setZoom] = useState(100);
    const [isPaused, setIsPaused] = useState(false);
    const [pdfError, setPdfError] = useState(null);
    const [loadAttempts, setLoadAttempts] = useState(0);
    const [numPages, setNumPages] = useState(null);

    const containerRef = useRef(null);
    const timerRef = useRef(null);
    const saveIntervalRef = useRef(null);
    const saveTimeoutRef = useRef(null);

    // Gestion du changement de module
    if (module?.id && module.id !== moduleKey) {
        console.log('🔍 [PDF] Module changé, chargement progression');
        const initial = loadProgressForModule(module.id);
        setModuleKey(module.id);
        setCurrentPage(initial.page);
        setTimeSpent(initial.time);
        setProgress(initial.progress);
        setTotalPages(0);
        setIsLoading(true);
        setPdfError(null);
        setLoadAttempts(0);
        setNumPages(null);
    }

    // Construction de l'URL finale
    const finalPdfUrl = useMemo(() => {
        console.log('🔍 [PDF] Calcul de finalPdfUrl - pdfUrl:', pdfUrl, 'mode:', mode);

        if (!pdfUrl) {
            console.warn('⚠️ [PDF] Aucune URL PDF fournie');
            return null;
        }

        if (pdfUrl.startsWith('http://') || pdfUrl.startsWith('https://') ||
            pdfUrl.includes('/api/') || pdfUrl.startsWith('/upload/')) {
            let finalUrl = pdfUrl;
            if (pdfToken && !finalUrl.includes('token=') && (finalUrl.includes('/api/') || finalUrl.includes('/stream'))) {
                const separator = finalUrl.includes('?') ? '&' : '?';
                finalUrl = `${finalUrl}${separator}token=${pdfToken}`;
            }
            console.log('🔍 [PDF] URL finale (non normalisée) :', finalUrl);
            return finalUrl;
        }

        let normalizedUrl = pdfUrl;
        if (!normalizedUrl.startsWith('/')) {
            normalizedUrl = `/${normalizedUrl}`;
        }
        if (!normalizedUrl.includes('/formationpdf/')) {
            normalizedUrl = `/upload/formationpdf${normalizedUrl}`;
        }
        if (mode === 'demo' && pdfToken && !normalizedUrl.includes('token=')) {
            const separator = normalizedUrl.includes('?') ? '&' : '?';
            normalizedUrl = `${normalizedUrl}${separator}token=${pdfToken}`;
        }
        console.log('🔍 [PDF] URL normalisée :', normalizedUrl);
        return normalizedUrl;
    }, [pdfUrl, pdfToken, mode]);

    const onDocumentLoadSuccess = ({ numPages }) => {
        console.log(`✅ [PDF] PDF chargé avec succès, pages: ${numPages}`);
        setNumPages(numPages);
        setTotalPages(numPages);
        setIsLoading(false);
        setPdfError(null);
        setLoadAttempts(0);
    };

    const onDocumentLoadError = (error) => {
        console.error('❌ [PDF] Erreur chargement PDF:', error);
        if (loadAttempts >= 2) {
            setIsLoading(false);
            setPdfError(`Impossible de charger le PDF. Vérifiez l'URL: ${finalPdfUrl}`);
        } else {
            setLoadAttempts(prev => prev + 1);
            console.log(`🔄 [PDF] Tentative ${loadAttempts + 1}/3, nouvel essai dans 2s...`);
            setTimeout(() => {
                setNumPages(null);
                setIsLoading(true);
            }, 2000);
        }
    };

    const saveProgressToLocalStorage = useCallback(() => {
        if (mode === 'demo') return;
        if (module?.id && !isLoading && totalPages > 0) {
            const progressValue = Math.min(100, Math.round((currentPage / totalPages) * 100));
            const progressData = {
                moduleId: module.id,
                page: currentPage,
                timeSpent: timeSpent,
                progress: progressValue,
                lastUpdated: new Date().toISOString()
            };
            localStorage.setItem(`module_progress_${module.id}`, JSON.stringify(progressData));
            setProgress(prev => prev !== progressValue ? progressValue : prev);
            console.log(`✅ Progression sauvegardée: ${progressValue}% pour le module ${module.id}`);
        }
    }, [module, currentPage, timeSpent, totalPages, isLoading, mode]);

    useEffect(() => {
        if (mode === 'demo') return;
        if (isOpen && !isLoading && module?.id && totalPages > 0) {
            if (saveTimeoutRef.current) clearTimeout(saveTimeoutRef.current);
            saveTimeoutRef.current = setTimeout(saveProgressToLocalStorage, 300);
        }
        return () => {
            if (saveTimeoutRef.current) clearTimeout(saveTimeoutRef.current);
        };
    }, [currentPage, zoom, isPaused, isLoading, isOpen, module?.id, totalPages, saveProgressToLocalStorage, mode]);

    useEffect(() => {
        if (mode === 'demo') return;
        if (isOpen && !isLoading && module?.id && totalPages > 0) {
            saveIntervalRef.current = setInterval(saveProgressToLocalStorage, 15000);
            return () => {
                if (saveIntervalRef.current) clearInterval(saveIntervalRef.current);
            };
        }
    }, [isOpen, isLoading, module?.id, totalPages, saveProgressToLocalStorage, mode]);

    useEffect(() => {
        return () => {
            if (mode !== 'demo' && module?.id && isOpen) {
                saveProgressToLocalStorage();
            }
        };
    }, [module?.id, isOpen, saveProgressToLocalStorage, mode]);

    useEffect(() => {
        if (mode === 'demo') return;
        if (isOpen && !isPaused && !isLoading) {
            timerRef.current = setInterval(() => {
                setTimeSpent(prev => prev + 1);
            }, 1000);
        }
        return () => {
            if (timerRef.current) clearInterval(timerRef.current);
        };
    }, [isOpen, isPaused, isLoading, mode]);

    const nextPage = useCallback(() => {
        if (totalPages > 0 && currentPage < totalPages) {
            setCurrentPage(prev => prev + 1);
        }
    }, [currentPage, totalPages]);

    const prevPage = useCallback(() => {
        if (currentPage > 1) {
            setCurrentPage(prev => prev - 1);
        }
    }, [currentPage]);

    const goToFirstPage = () => {
        if (currentPage > 1) setCurrentPage(1);
    };

    const zoomIn = () => setZoom(prev => Math.min(200, prev + 10));
    const zoomOut = () => setZoom(prev => Math.max(50, prev - 10));

    const toggleFullscreen = () => {
        if (!document.fullscreenElement) {
            containerRef.current?.requestFullscreen();
            setIsFullscreen(true);
        } else {
            document.exitFullscreen();
            setIsFullscreen(false);
        }
    };

    const handleModuleChange = (direction) => {
        if (mode !== 'demo') {
            saveProgressToLocalStorage();
        }
        if (onModuleChange) {
            if (onComplete && direction > 0) {
                onComplete(module?.id);
            }
            onModuleChange(direction);
            setModuleKey(null);
            setCurrentPage(1);
            setTotalPages(0);
            setTimeSpent(0);
            setIsLoading(true);
            setPdfError(null);
            setLoadAttempts(0);
            setNumPages(null);
        }
    };

    const goToPreviousModule = () => {
        if (moduleOrdre > 1) {
            handleModuleChange(-1);
        }
    };

    const goToNextModule = () => {
        if (moduleOrdre < totalModules) {
            handleModuleChange(1);
        }
    };

    const handleTerminer = () => {
        if (mode === 'demo') {
            onClose();
            return;
        }
        if (module?.id) {
            const existing = localStorage.getItem(`module_progress_${module.id}`);
            let progressData = {};
            if (existing) {
                try { progressData = JSON.parse(existing); } catch (e) { console.error(e); }
            }
            progressData.completed = true;
            progressData.completedAt = new Date().toISOString();
            localStorage.setItem(`module_progress_${module.id}`, JSON.stringify(progressData));
        }
        saveProgressToLocalStorage();
        if (onComplete) onComplete(module?.id);
        if (onClose) onClose();
    };

    const formatTime = (seconds) => {
        const h = Math.floor(seconds / 3600);
        const m = Math.floor((seconds % 3600) / 60);
        const s = seconds % 60;
        if (h > 0) {
            return `${h}h ${m.toString().padStart(2, '0')}min ${s.toString().padStart(2, '0')}s`;
        }
        return `${m.toString().padStart(2, '0')}:${s.toString().padStart(2, '0')}`;
    };

    const hasAccess = () => {
        if (mode === 'demo') return true;
        const allowedRoles = ['etudiant', 'client', 'admin', 'superadmin'];
        return allowedRoles.includes(userRole?.toLowerCase());
    };

    const isLastModule = moduleOrdre >= totalModules;
    const isLastPage = currentPage >= totalPages && totalPages > 0;
    const isComplete = isLastModule && isLastPage && totalPages > 0;

    if (!isOpen || !module) return null;

    if (!hasAccess()) {
        return (
            <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 backdrop-blur-sm">
                <div className="bg-white p-4 sm:p-8 rounded-2xl shadow-2xl max-w-md w-full mx-2 text-center">
                    <div className="text-5xl sm:text-6xl mb-4">🔒</div>
                    <h3 className="text-base sm:text-xl font-serif font-bold text-gray-800">Accès refusé</h3>
                    <p className="text-xs sm:text-sm text-gray-600 font-serif mt-2">Vous n'avez pas les droits nécessaires pour accéder à ce contenu.</p>
                    <button onClick={onClose} className="mt-4 sm:mt-6 px-4 py-2 text-xs sm:text-sm bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition">Fermer</button>
                </div>
            </div>
        );
    }

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 backdrop-blur-sm">
            <div ref={containerRef} className="relative w-full max-w-6xl h-[95vh] bg-white rounded-2xl shadow-2xl overflow-hidden flex flex-col">
                <div className="bg-green-600 text-white p-2 sm:p-3 flex items-center justify-between flex-shrink-0">
                    <div className="flex items-center gap-1.5 sm:gap-3 flex-1 min-w-0">
                        <button onClick={onClose} className="p-1.5 hover:bg-green-700 rounded-lg transition"><X className="w-4 h-4 sm:w-5 sm:h-5" /></button>
                        <div className="flex items-center gap-1.5 sm:gap-2">
                            <BookOpen className="w-4 h-4 sm:w-[18px] sm:h-[18px] text-green-200" />
                            <span className="font-serif font-bold text-[10px] sm:text-sm truncate">{formationTitre || 'Formation'}</span>
                            <span className="text-[8px] sm:text-xs text-green-200 bg-green-700/50 px-1.5 py-0.5 rounded-full">Module {moduleOrdre}/{totalModules}</span>
                        </div>
                        {mode === 'demo' && <span className="text-[8px] sm:text-xs bg-yellow-500/50 text-white px-1.5 py-0.5 rounded-full ml-1 sm:ml-2">🎯 Démo</span>}
                        {progress > 0 && mode !== 'demo' && (
                            <div className="flex items-center gap-0.5 sm:gap-1 ml-1 sm:ml-2">
                                <div className="w-12 sm:w-20 h-1.5 bg-green-700/50 rounded-full overflow-hidden">
                                    <div className="h-full bg-white rounded-full transition-all duration-300" style={{ width: `${progress}%` }} />
                                </div>
                                <span className="text-[8px] sm:text-xs text-green-200">{progress}%</span>
                            </div>
                        )}
                        {isComplete && mode !== 'demo' && (
                            <span className="text-[8px] sm:text-xs bg-yellow-400 text-green-900 px-1.5 py-0.5 rounded-full font-bold ml-1 sm:ml-2">✅ Terminé !</span>
                        )}
                    </div>
                    <div className="flex items-center gap-1.5 sm:gap-3 flex-shrink-0">
                        {mode !== 'demo' && (
                            <div className="flex items-center gap-0.5 sm:gap-1.5 bg-green-700/50 px-1.5 py-0.5 sm:px-3 sm:py-1 rounded-full">
                                <Clock className="w-3 h-3 sm:w-4 sm:h-4 text-green-200" />
                                <span className="font-mono text-[8px] sm:text-sm font-medium">{formatTime(timeSpent)}</span>
                            </div>
                        )}
                        <button onClick={() => setIsPaused(!isPaused)} className={`p-1.5 rounded-lg transition ${isPaused ? 'bg-yellow-500/50 hover:bg-yellow-500' : 'hover:bg-green-700'}`}>
                            {isPaused ? <ChevronDown className="w-3 h-3 sm:w-4 sm:h-4" /> : <ChevronUp className="w-3 h-3 sm:w-4 sm:h-4" />}
                        </button>
                        <button onClick={toggleFullscreen} className="p-1.5 hover:bg-green-700 rounded-lg transition">
                            {isFullscreen ? <Minimize2 className="w-4 h-4 sm:w-[18px] sm:h-[18px]" /> : <Maximize2 className="w-4 h-4 sm:w-[18px] sm:h-[18px]" />}
                        </button>
                    </div>
                </div>

                <div className="flex-1 relative bg-gray-100 overflow-y-auto flex flex-col items-center p-2 sm:p-4">
                    {isLoading && (
                        <div className="absolute inset-0 flex items-center justify-center bg-gray-100 z-10">
                            <div className="text-center px-4">
                                <div className="w-12 h-12 sm:w-16 sm:h-16 border-4 border-green-500 border-t-transparent rounded-full animate-spin mx-auto"></div>
                                <p className="mt-2 sm:mt-4 text-xs sm:text-sm text-gray-600 font-serif">Chargement du PDF...</p>
                                <p className="text-[8px] sm:text-xs text-gray-400 font-serif mt-1 break-all">{finalPdfUrl || 'Aucune URL disponible'}</p>
                                <p className="text-[8px] sm:text-xs text-gray-400 font-serif mt-0.5">Tentative {loadAttempts + 1}/3</p>
                            </div>
                        </div>
                    )}

                    {pdfError && (
                        <div className="absolute inset-0 flex items-center justify-center bg-gray-100 z-10 p-4">
                            <div className="text-center max-w-md w-full p-4 sm:p-6 bg-white rounded-xl shadow-lg">
                                <div className="text-5xl sm:text-6xl mb-4">❌</div>
                                <p className="text-xs sm:text-sm text-red-600 font-serif font-semibold">{pdfError}</p>
                                <p className="text-[8px] sm:text-xs text-gray-500 mt-1 font-mono break-all">URL: {finalPdfUrl || 'Non définie'}</p>
                                <div className="flex flex-wrap gap-1.5 sm:gap-2 justify-center mt-3 sm:mt-4">
                                    <button onClick={() => window.open(finalPdfUrl, '_blank')} className="px-2 py-1 sm:px-4 sm:py-2 text-[8px] sm:text-sm bg-green-600 text-white rounded-lg hover:bg-green-700 transition">📄 Ouvrir</button>
                                    <button onClick={() => { setIsLoading(true); setPdfError(null); setLoadAttempts(0); setNumPages(null); }} className="px-2 py-1 sm:px-4 sm:py-2 text-[8px] sm:text-sm bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition">🔄 Réessayer</button>
                                </div>
                            </div>
                        </div>
                    )}

                    {finalPdfUrl && !pdfError && (
                        <Document
                            file={finalPdfUrl}
                            onLoadSuccess={onDocumentLoadSuccess}
                            onLoadError={onDocumentLoadError}
                            className="flex flex-col items-center w-full"
                        >
                            {numPages && (
                                <Page
                                    pageNumber={currentPage}
                                    scale={zoom / 100}
                                    className="shadow-lg"
                                    renderTextLayer={false}
                                    renderAnnotationLayer={false}
                                />
                            )}
                        </Document>
                    )}
                </div>

                <div className="bg-gray-50 border-t border-gray-200 p-1.5 sm:p-2 flex flex-col gap-1 flex-shrink-0">
                    <div className="flex items-center justify-center gap-1 sm:gap-2 bg-white rounded-lg px-1.5 py-0.5 sm:px-3 sm:py-1 shadow-sm border border-gray-200 mx-auto">
                        <span className="text-[7px] sm:text-xs font-medium text-gray-600">Page :</span>
                        <button
                            onClick={goToFirstPage}
                            disabled={currentPage === 1 || isLoading}
                            className="p-1 sm:p-1.5 text-gray-600 hover:text-gray-800 hover:bg-gray-100 rounded disabled:opacity-30 transition"
                            title="Première page"
                        >
                            <Home className="w-3.5 h-3.5 sm:w-[18px] sm:h-[18px]" />
                        </button>
                        <button
                            onClick={prevPage}
                            disabled={currentPage <= 1 || isLoading}
                            className="p-1 sm:p-1.5 text-gray-600 hover:text-gray-800 hover:bg-gray-100 rounded disabled:opacity-30 transition"
                            title="Page précédente"
                        >
                            <ChevronLeft className="w-3.5 h-3.5 sm:w-[18px] sm:h-[18px]" />
                        </button>
                        <span className="text-[8px] sm:text-xs font-bold text-gray-700 px-0.5 sm:px-1 min-w-[40px] text-center">
                            {currentPage} / {totalPages || '?'}
                        </span>
                        <button
                            onClick={nextPage}
                            disabled={(totalPages > 0 && currentPage >= totalPages) || isLoading}
                            className="p-1 sm:p-1.5 text-gray-600 hover:text-gray-800 hover:bg-gray-100 rounded disabled:opacity-30 transition"
                            title="Page suivante"
                        >
                            <ChevronRight className="w-3.5 h-3.5 sm:w-[18px] sm:h-[18px]" />
                        </button>
                        {isLastPage && mode !== 'demo' && (
                            <button
                                onClick={handleTerminer}
                                className="px-2 py-1 sm:px-3 sm:py-1 text-[7px] sm:text-xs font-semibold text-white bg-green-700 hover:bg-green-800 rounded-lg transition flex items-center gap-0.5 sm:gap-1"
                                title="Terminer le module"
                            >
                                <LogOut className="w-3 h-3 sm:w-4 sm:h-4" /> Terminer
                            </button>
                        )}
                        {isLastPage && mode === 'demo' && (
                            <button
                                onClick={onClose}
                                className="px-2 py-1 sm:px-3 sm:py-1 text-[7px] sm:text-xs font-semibold text-white bg-blue-600 hover:bg-blue-700 rounded-lg transition flex items-center gap-0.5 sm:gap-1"
                                title="Fermer"
                            >
                                <X className="w-3 h-3 sm:w-4 sm:h-4" /> Fermer
                            </button>
                        )}
                        <div className="flex items-center gap-0.5 sm:gap-1 ml-1 sm:ml-2">
                            <button onClick={zoomOut} className="p-0.5 sm:p-1 hover:bg-gray-200 rounded transition" title="Dézoomer"><ZoomOut className="w-3 h-3 sm:w-4 sm:h-4" /></button>
                            <span className="text-[7px] sm:text-xs text-gray-600 w-7 sm:w-10 text-center">{zoom}%</span>
                            <button onClick={zoomIn} className="p-0.5 sm:p-1 hover:bg-gray-200 rounded transition" title="Zoomer"><ZoomIn className="w-3 h-3 sm:w-4 sm:h-4" /></button>
                        </div>
                    </div>

                    <div className="flex items-center justify-end gap-1 sm:gap-2 flex-wrap">
                        <span className="text-[8px] sm:text-xs text-gray-500 font-serif">Module {moduleOrdre}/{totalModules}</span>
                        {progress > 0 && mode !== 'demo' && (
                            <div className="flex items-center gap-0.5 sm:gap-1">
                                <div className="w-12 sm:w-24 h-1.5 bg-gray-200 rounded-full overflow-hidden">
                                    <div className="h-full bg-green-500 rounded-full transition-all duration-300" style={{ width: `${progress}%` }} />
                                </div>
                                <span className="text-[7px] sm:text-[10px] text-gray-400">{progress}%</span>
                            </div>
                        )}
                        {mode === 'demo' && (
                            <span className="text-[7px] sm:text-[10px] text-yellow-600 bg-yellow-100 px-1.5 py-0.5 rounded-full">🔍 Aperçu</span>
                        )}
                        <div className="w-px h-4 sm:h-6 bg-gray-300 mx-0.5 sm:mx-1" />
                        <button
                            onClick={goToPreviousModule}
                            disabled={moduleOrdre <= 1 || isLoading}
                            className="px-2 py-1 sm:px-4 sm:py-1.5 text-xs sm:text-sm font-serif font-medium bg-gray-200 text-gray-800 hover:bg-gray-300 rounded-lg disabled:opacity-30 transition flex items-center gap-0.5 sm:gap-1"
                            title="Module précédent"
                        >
                            <ChevronLeft className="w-3.5 h-3.5 sm:w-4 sm:h-4" /> Précédent
                        </button>
                        <button
                            onClick={goToNextModule}
                            disabled={moduleOrdre >= totalModules || isLoading}
                            className="px-2 py-1 sm:px-4 sm:py-1.5 text-xs sm:text-sm font-serif font-medium bg-green-600 text-white hover:bg-green-700 rounded-lg disabled:opacity-30 transition flex items-center gap-0.5 sm:gap-1"
                            title="Module suivant"
                        >
                            Suivant <ChevronRight className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
                        </button>
                        {mode !== 'demo' && (
                            <>
                                <div className="w-px h-4 sm:h-6 bg-gray-300 mx-0.5 sm:mx-1" />
                                <button
                                    onClick={saveProgressToLocalStorage}
                                    className="px-2 py-1 sm:px-3 sm:py-1.5 text-[7px] sm:text-xs bg-blue-100 text-blue-700 hover:bg-blue-200 rounded-lg transition flex items-center gap-0.5 sm:gap-1"
                                    title="Sauvegarder la progression"
                                >
                                    💾 Sauvegarder
                                </button>
                            </>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
}